import { supabase } from "@/integrations/supabase/client";
import type { OnboardingPersistence, OnboardingPersistedState } from "@flowstate/onboarding";

/**
 * Supabase-backed persistence for onboarding showcases.
 * Stores completion state in the user's profile metadata.
 *
 * Uses Supabase auth.users metadata (no extra table needed).
 * Falls back to localStorage for unauthenticated users.
 */
export function createSupabasePersistence(): OnboardingPersistence {
  const STORAGE_KEY = "flowstate:onboarding:";

  return {
    async load(flowId: string): Promise<OnboardingPersistedState | null> {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          // Fallback to localStorage for anonymous users
          const raw = localStorage.getItem(STORAGE_KEY + flowId);
          return raw ? JSON.parse(raw) : null;
        }

        const meta = user.user_metadata?.onboarding_flows as Record<string, OnboardingPersistedState> | undefined;
        return meta?.[flowId] ?? null;
      } catch {
        return null;
      }
    },

    async save(flowId: string, state: OnboardingPersistedState): Promise<void> {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          localStorage.setItem(STORAGE_KEY + flowId, JSON.stringify(state));
          return;
        }

        const existing = (user.user_metadata?.onboarding_flows ?? {}) as Record<string, OnboardingPersistedState>;
        await supabase.auth.updateUser({
          data: {
            onboarding_flows: {
              ...existing,
              [flowId]: state,
            },
          },
        });
      } catch {
        // Best-effort — don't break the UI
      }
    },

    async clear(flowId: string): Promise<void> {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          localStorage.removeItem(STORAGE_KEY + flowId);
          return;
        }

        const existing = (user.user_metadata?.onboarding_flows ?? {}) as Record<string, OnboardingPersistedState>;
        const { [flowId]: _, ...rest } = existing;
        await supabase.auth.updateUser({
          data: {
            onboarding_flows: rest,
          },
        });
      } catch {
        // Best-effort
      }
    },
  };
}
